import type {
  CurrencyItem,
  DateFormatItem,
  SitePreferenceRecord,
  SitePreferenceWritePayload,
  TimeFormatItem,
  TimeZoneItem,
} from '../../../common/models';
import { patternWithDescription } from '../../utils/referenceLabels';

/**
 * Mock backend for step 2, shaped to mdfiles/site-config.md.
 *
 * Stateful, like the step 1 fixture: the site-preference row does not exist
 * until a save creates it. That is what lets mock mode exercise the
 * documented GET-then-POST-or-PUT decision rather than only one branch of it.
 */

/* -- reference data (section 4) -------------------------------------------- */

const CURRENCIES: readonly CurrencyItem[] = [
  { id: 5, currencyCode: 'USD', currencyName: 'US Dollar', isDefault: false },
  { id: 6, currencyCode: 'EUR', currencyName: 'Euro', isDefault: false },
  { id: 7, currencyCode: 'INR', currencyName: 'Indian Rupee', isDefault: true },
  { id: 8, currencyCode: 'GBP', currencyName: 'British Pound', isDefault: false },
];

/*
  Mirrors the rows V20260928150000 leaves in the three format masters: the
  seeded ids are unchanged, and `label` is what the platform composes for each
  (SiteFormatLabels) -- the dropdowns show it as sent.
*/

function dateFormat(
  id: number,
  datePattern: string,
  dateFormatText: string,
  isDefault = false,
): DateFormatItem {
  return {
    id,
    dateFormat: dateFormatText,
    datePattern,
    label: patternWithDescription(datePattern, dateFormatText),
    isDefault,
  };
}

function timeFormat(
  id: number,
  timePattern: string,
  timeFormatText: string,
  isDefault = false,
): TimeFormatItem {
  return {
    id,
    timeFormat: timeFormatText,
    timePattern,
    label: patternWithDescription(timePattern, timeFormatText),
    isDefault,
  };
}

const DATE_FORMATS: readonly DateFormatItem[] = [
  dateFormat(5, 'DD/MM/YYYY', 'Day, Month & Four Digit Year', true),
  dateFormat(6, 'MM/DD/YYYY', 'Month, Day & Four Digit Year'),
  dateFormat(7, 'YYYY-MM-DD', 'Four Digit Year, Month & Day'),
  dateFormat(8, 'DD-MM-YYYY', 'Day, Month & Four Digit Year'),
  dateFormat(9, 'MM-DD-YYYY', 'Month, Day & Four Digit Year'),
  dateFormat(10, 'YYYY/MM/DD', 'Four Digit Year, Month & Day'),
  dateFormat(11, 'DD.MM.YYYY', 'Day, Month & Four Digit Year'),
  dateFormat(12, 'DD-MMM-YY', 'Day, Abbreviated Month & Two Digit Year'),
  dateFormat(13, 'DD-MM-YY', 'Day, Month & Two Digit Year'),
  dateFormat(14, 'MM-DD-YY', 'Month, Day & Two Digit Year'),
  dateFormat(15, 'YY-MM-DD', 'Two Digit Year, Month & Day'),
  dateFormat(16, 'DD/MM/YY', 'Day, Month & Two Digit Year'),
  dateFormat(17, 'MM/DD/YY', 'Month, Day & Two Digit Year'),
  dateFormat(18, 'DD.MM.YY', 'Day, Month & Two Digit Year'),
];

const TIME_FORMATS: readonly TimeFormatItem[] = [
  timeFormat(4, 'HH:mm:ss', '24-hour format', true),
  timeFormat(5, 'hh:mm A', '12-hour format with AM/PM without seconds'),
  timeFormat(6, 'HH:mm', '24-hour format without seconds'),
  timeFormat(7, 'hh:mm:ss A', '12-hour format with AM/PM'),
];

/** [id, name, IANA identifier, the label the platform derives from it]. */
const ZONE_ROWS: readonly (readonly [number, string, string, string])[] = [
  [5, 'India Standard Time', 'Asia/Kolkata', 'IST (+5:30)'],
  [6, 'Coordinated Universal Time', 'UTC', 'UTC (+0:00)'],
  [7, 'Eastern Time', 'America/New_York', 'EST (-5:00)'],
  [8, 'Central European Time', 'Europe/Berlin', 'CET (+1:00)'],
  [9, 'Hawaii-Aleutian Standard Time', 'Pacific/Honolulu', 'HST (-10:00)'],
  [10, 'Alaska Standard Time', 'America/Anchorage', 'AKST (-9:00)'],
  [11, 'Pacific Standard Time', 'America/Los_Angeles', 'PST (-8:00)'],
  [12, 'Mountain Standard Time', 'America/Denver', 'MST (-7:00)'],
  [13, 'Central Standard Time', 'America/Chicago', 'CST (-6:00)'],
  [14, 'Atlantic Standard Time', 'America/Halifax', 'AST (-4:00)'],
  [15, 'Brasilia Time', 'America/Sao_Paulo', 'BRT (-3:00)'],
  [16, 'Greenwich Mean Time', 'Europe/London', 'GMT (+0:00)'],
  [17, 'Eastern European Time', 'Europe/Athens', 'EET (+2:00)'],
  [18, 'South Africa Standard Time', 'Africa/Johannesburg', 'SAST (+2:00)'],
  [19, 'Moscow Standard Time', 'Europe/Moscow', 'MSK (+3:00)'],
  [20, 'Gulf Standard Time', 'Asia/Dubai', 'GST (+4:00)'],
  [21, 'Pakistan Standard Time', 'Asia/Karachi', 'PKT (+5:00)'],
  [22, 'Nepal Time', 'Asia/Kathmandu', 'NPT (+5:45)'],
  [23, 'Bangladesh Standard Time', 'Asia/Dhaka', 'BST (+6:00)'],
  [24, 'Indochina Time', 'Asia/Bangkok', 'ICT (+7:00)'],
  [25, 'Singapore Standard Time', 'Asia/Singapore', 'SGT (+8:00)'],
  [26, 'China Standard Time', 'Asia/Shanghai', 'CST (+8:00)'],
  [27, 'Japan Standard Time', 'Asia/Tokyo', 'JST (+9:00)'],
  [28, 'Australian Eastern Standard Time', 'Australia/Sydney', 'AEST (+10:00)'],
  [29, 'New Zealand Standard Time', 'Pacific/Auckland', 'NZST (+12:00)'],
];

const TIME_ZONES: readonly TimeZoneItem[] = ZONE_ROWS.map(([id, timeZone, timezoneIdentifier, label]) => ({
  id,
  timeZone,
  timezoneIdentifier,
  label,
  isDefault: id === 5,
}));

export function mockCurrencies(): CurrencyItem[] {
  return [...CURRENCIES];
}

export function mockDateFormats(): DateFormatItem[] {
  return [...DATE_FORMATS];
}

export function mockTimeFormats(): TimeFormatItem[] {
  return [...TIME_FORMATS];
}

export function mockTimeZones(): TimeZoneItem[] {
  return [...TIME_ZONES];
}

/* -- site preference store (section 5) ------------------------------------- */

let preferences = new Map<string, SitePreferenceRecord>();
let nextId = 0;

function labelOf<T extends { id: number }>(
  list: readonly T[],
  id: number | null,
  pick: (item: T) => string,
): string | null {
  const found = list.find((item) => item.id === id);
  return found ? pick(found) : null;
}

function toRecord(
  base: { id: number; createdDate: string },
  payload: SitePreferenceWritePayload,
): SitePreferenceRecord {
  const now = new Date().toISOString().replace(/Z$/, '');

  return {
    id: base.id,
    organizationId: 1,
    // Always null: this row is the organization-wide default (section 5.1).
    legalEntityId: null,
    currencyId: payload.currencyId,
    currency: labelOf(CURRENCIES, payload.currencyId, (item) => item.currencyCode),
    dateFormatId: payload.dateFormatId,
    dateFormat: labelOf(DATE_FORMATS, payload.dateFormatId, (item) => item.dateFormat),
    timeFormatId: payload.timeFormatId,
    timeFormat: labelOf(TIME_FORMATS, payload.timeFormatId, (item) => item.timeFormat),
    timeZoneId: payload.timeZoneId,
    timeZone: labelOf(TIME_ZONES, payload.timeZoneId, (item) => item.timeZone),
    // A real object, not a stringified blob.
    configurations: { theme: { ...payload.theme } },
    loginImage: payload.loginImage,
  loginImageName: payload.loginImageName,
    createdBy: 2,
    createdDate: base.createdDate,
    modifiedBy: 2,
    modifiedDate: now,
  };
}

/** Null stands in for the documented 404 -- step 2 not saved yet. */
export function mockGetSitePreference(organizationCode: string): SitePreferenceRecord | null {
  const found = preferences.get(organizationCode);
  return found ? { ...found } : null;
}

export function mockCreateSitePreference(
  organizationCode: string,
  payload: SitePreferenceWritePayload,
): SitePreferenceRecord {
  nextId += 1;
  const created = toRecord(
    { id: nextId, createdDate: new Date().toISOString().replace(/Z$/, '') },
    payload,
  );
  preferences.set(organizationCode, created);
  return { ...created };
}

export function mockUpdateSitePreference(
  organizationCode: string,
  payload: SitePreferenceWritePayload,
): SitePreferenceRecord {
  const existing = preferences.get(organizationCode);
  const updated = toRecord(
    {
      id: existing?.id ?? ++nextId,
      createdDate: existing?.createdDate ?? new Date().toISOString().replace(/Z$/, ''),
    },
    payload,
  );
  preferences.set(organizationCode, updated);
  return { ...updated };
}

/** Test-only reset, so one case's save cannot leak into the next. */
export function resetMockSitePreferences(): void {
  preferences = new Map();
  nextId = 0;
}
